import cx from 'classnames';
import React, { CSSProperties } from 'react';

import { useRobotSelector, useEditor } from '../context';
import { ROBOT_COMMAND_TYPE, ROBOT_COORD, ROBOT_WALL } from '../robot.constants';
import {
    RobotCommand,
    RobotCommandRoboMove,
    RobotCommandSetCellColor,
    RobotPosition,
    RobotProblem,
    RobotSkinSample,
    EDITOR_TYPES,
} from '../robot.types';
import { useSkinForSample } from '../utils';

import FieldScale from './robot__field-scale';
import { useSprite } from './robot__field-sprite';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './robot__field.module.scss';
import { cellPosition, cells, getResourceUrl, getRobotView, px } from './utils';

interface Props {
    direction?: string | null;
    field: RobotCommand[];
    skin?: RobotSkinSample;
    resources?: RobotProblem['resources'];
    position?: RobotCommandRoboMove;
    coloredCells?: RobotCommandSetCellColor[];
    crash?: RobotPosition;
    speed?: number[];
    editor: EDITOR_TYPES | null;
    hideFinish?: boolean;
    /** Чтобы гермиона не моргала */
    disableAnimation?: boolean;
}

/**
 * Сам компонент с полем, который получает пропсы от контейнера
 * Для тестов и сторибука используется RobotPreview
 */
export const Field = ({ disableAnimation, direction, field, position, resources, editor, ...props }: Props) => {
    const hideFinish = props.hideFinish;
    const skin = props.skin || {};
    const robotView = getRobotView(skin.robot_view, { isLightEditor: editor === EDITOR_TYPES.LIGHT_PYTHON });
    const isCodeLightV2 = editor && editor === EDITOR_TYPES.CODE_LIGHT_V2; //выпилить в рамках EDUCATION-33123
    const sprites = skin.sprites || {};
    const spritesOptions = skin.sprites_options;
    const robotViewUrl = skin.robot_view_url;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const testRobotSpeed = typeof window !== 'undefined' && window.__testRobotSpeed;
    const speed = testRobotSpeed || props.speed?.[0] || null;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const cssProps: CSSProperties = {
        ...(speed && { '--speed': speed }),
        ...(skin.color && { '--bg-color': skin.color }),
        ...(skin.border && { '--bg-border': skin.border }),
        ...(skin.walls?.color && { '--walls-color': skin.walls?.color }),
        ...(px(skin.walls?.width) && { '--walls-width': px(skin.walls?.width) }),
        ...(skin.hide_grid && { '--walls-width': 0 }),
        ...(px(skin.grid?.width) && { '--grid-width': px(skin.grid?.width) }),
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const cssRobotViewProps: CSSProperties = {
        ...(robotViewUrl && { 'background-image': `url('${robotViewUrl}')` }),
    };

    // todo over abstracted
    const FieldSprite = useSprite(resources);

    return (
        <FieldScale editor={editor} style={cssProps}>
            <div className={cx(styles.grid, 'qa_robot-grid')}>
                {field.map((command, i) => {
                    switch (command.type) {
                        case ROBOT_COMMAND_TYPE.CREATE_FIELD:
                            return (
                                <React.Fragment key="field">
                                    <div
                                        className={cx(styles.field, { [styles['field_hide-grid']]: skin.hide_grid })}
                                        style={{
                                            width: cells(command[ROBOT_COORD.HORIZONTAL]),
                                            height: cells(command[ROBOT_COORD.VERTICAL]),
                                        }}
                                    />
                                    {props.coloredCells?.map((cell, j) => (
                                        <FieldSprite
                                            className={styles.color}
                                            key={j}
                                            options={spritesOptions?.[`colors:${cell.color}`]}
                                            position={cell}
                                            skin={sprites?.[`colors:${cell.color}`]}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        case ROBOT_COMMAND_TYPE.SET_CELL_COLOR:
                            return (
                                <FieldSprite
                                    className={styles.color}
                                    key={i}
                                    options={spritesOptions?.[`colors:${command.color}`]}
                                    position={command}
                                    skin={sprites?.[`colors:${command.color}`]}
                                />
                            );
                        case ROBOT_COMMAND_TYPE.CREATE_WALLS:
                            return (
                                <div
                                    className={cx(styles.walls, {
                                        [styles.wall_left]: command.sides & ROBOT_WALL.LEFT,
                                        [styles.wall_right]: command.sides & ROBOT_WALL.RIGHT,
                                        [styles.wall_top]: command.sides & ROBOT_WALL.TOP,
                                        [styles.wall_bottom]: command.sides & ROBOT_WALL.BOTTOM,
                                    })}
                                    key={i}
                                    style={cellPosition(command)}
                                />
                            );
                        case ROBOT_COMMAND_TYPE.CREATE_LABEL:
                            return (
                                <FieldSprite
                                    className={cx(styles.label)}
                                    key={i}
                                    position={command}
                                    skin={sprites?.[`labels:${command.label}`]}
                                />
                            );
                        case ROBOT_COMMAND_TYPE.CREATE_PARKING_POINT:
                            return (
                                !hideFinish && (
                                    <FieldSprite
                                        className={cx(styles.finish, { [styles.finish_custom_z_index]: isCodeLightV2 })}
                                        key={i}
                                        position={command}
                                        skin={sprites?.finish}
                                    />
                                )
                            );
                        case ROBOT_COMMAND_TYPE.ROBO_MOVE:
                            return (
                                <React.Fragment key={`start+robot-${i}`}>
                                    <FieldSprite
                                        className={cx(styles.start)}
                                        position={command}
                                        skin={sprites?.start}
                                    />
                                    <FieldSprite
                                        className={cx(
                                            styles.robot,
                                            styles.robot_moving,
                                            'qa_robot-sprite',
                                            {
                                                [styles.robot_custom_z_index]: isCodeLightV2,
                                            },
                                            disableAnimation && styles['disable-animation']
                                        )}
                                        position={position || command}
                                    >
                                        <div
                                            className={cx(
                                                styles.sprite,
                                                styles['robot-body'],
                                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                // @ts-ignore
                                                styles[`robot-body-${robotViewUrl ? 'view' : robotView}`],
                                                { [styles[`robot-body-${direction}`]]: Boolean(robotViewUrl) },
                                                { [styles['robot-body_crash']]: Boolean(props.crash) }
                                            )}
                                            style={cssRobotViewProps}
                                        />
                                        <div
                                            className={cx(
                                                styles.sprite,
                                                styles['robot-base'],
                                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                // @ts-ignore
                                                styles[`robot-base-${robotViewUrl ? 'view' : robotView}`],
                                                { [styles['robot-base_crash']]: Boolean(props.crash) }
                                            )}
                                        />
                                    </FieldSprite>
                                </React.Fragment>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
            {skin.backgrounds?.map((bg, i) => (
                <div
                    className={styles.field_background}
                    key={i}
                    style={{ backgroundImage: getResourceUrl(resources, bg) }}
                />
            ))}
            {skin.foregrounds?.map((fg, i) => (
                <div
                    className={styles.field_foreground}
                    key={i}
                    style={{ backgroundImage: getResourceUrl(resources, fg) }}
                />
            ))}
        </FieldScale>
    );
};

const FieldMemo = React.memo(Field);

/** Контейнер поля, получающий для него пропсы */
const RobotField = () => {
    const sample = useRobotSelector((s) => s.sample);
    const selectedSample = useRobotSelector((s) => s.samples[s.sample]);
    const skin = useRobotSelector((s) => s.problem.markup.options.skin);
    const skinSample = useSkinForSample(skin?.samples, sample);
    const resources = useRobotSelector((s) => s.problem.resources);
    const { editor } = useEditor();
    const line = useRobotSelector((s) => s.state.position?.l);
    const lineNumber = useRobotSelector((s) => s.state.lineNumber);
    const crash = useRobotSelector((s) => s.state.crash);
    const commands = useRobotSelector((s) => s.code?.answer?.code || '')
        .slice(0, -2)
        .split('()\n');
    const { key, ...robotRunState } = useRobotSelector((s) => ({
        key: s.state.key,
        speed: s.state.speed,
        position: s.state.position,
        coloredCells: s.state.coloredCells,
        crash: s.state.crash,
    }));

    const getStep = () => {
        if (crash) {
            return lineNumber;
        } else if (line) {
            return line;
        }
        return undefined;
    };

    const step = getStep();

    if (selectedSample.hidden) {
        return null;
    }

    return (
        <FieldMemo
            {...robotRunState}
            direction={step ? commands[step - 1] : null}
            editor={editor}
            field={selectedSample.field}
            hideFinish={skin?.hide_finish}
            key={`${sample}:${key}`}
            resources={resources}
            skin={skinSample}
        />
    );
};

export default RobotField;
